/* ============================================================
   PINC Landing Page — Interactive behavior
   Vanilla JS, no dependencies. Progressive enhancement:
   the page is fully readable without JS; this layer just adds
   tabs, accordion, pricing toggle, scroll animations, and cart.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Scroll-in animations ---------- */
  function initFades() {
    var els = document.querySelectorAll('.pinc-fade, .pinc-fade-up, .pinc-fade-left, .pinc-fade-right');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach(function (el) { obs.observe(el); });
  }

  /* ---------- Sticky-nav scrolled state ---------- */
  function initNav() {
    var nav = document.querySelector('[data-pinc-nav]');
    if (!nav) return;
    var onScroll = function () {
      if (window.scrollY > 50) nav.classList.add('is-scrolled');
      else nav.classList.remove('is-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Ingredients tabs ---------- */
  function initIngredientTabs(root) {
    var tabs = root.querySelectorAll('[data-pinc-ing-tab]');
    var panels = root.querySelectorAll('[data-pinc-ing-panel]');
    if (!tabs.length) return;

    function activate(idx) {
      tabs.forEach(function (t, i) {
        var selected = i === idx;
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) {
          var color = t.getAttribute('data-color') || '#008f83';
          t.style.setProperty('--pinc-ing-color', color);
        }
      });
      panels.forEach(function (p, i) {
        if (i === idx) p.removeAttribute('hidden');
        else p.setAttribute('hidden', '');
      });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { activate(i); });
      tab.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          var next = (i + 1) % tabs.length;
          tabs[next].focus();
          activate(next);
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var prev = (i - 1 + tabs.length) % tabs.length;
          tabs[prev].focus();
          activate(prev);
        }
      });
    });

    activate(0);
  }

  /* ---------- Pricing plan toggle ---------- */
  function initPricingToggle(root) {
    var btns = root.querySelectorAll('[data-pinc-plan-btn]');
    var cards = root.querySelectorAll('[data-pinc-plan]');
    var card = root.querySelector('[data-pinc-pricing-card]');
    if (!btns.length) return;

    function switchTo(plan) {
      btns.forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-pinc-plan-btn') === plan ? 'true' : 'false');
      });
      cards.forEach(function (c) {
        if (c.getAttribute('data-pinc-plan') === plan) c.removeAttribute('hidden');
        else c.setAttribute('hidden', '');
      });
      if (card) card.setAttribute('data-plan', plan);
    }

    btns.forEach(function (b) {
      b.addEventListener('click', function () {
        switchTo(b.getAttribute('data-pinc-plan-btn'));
      });
    });
  }

  /* ---------- FAQ accordion ---------- */
  function initFaq(root) {
    var btns = root.querySelectorAll('[data-pinc-faq-btn]');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        var panelId = btn.getAttribute('aria-controls');
        var panel = panelId ? document.getElementById(panelId) : null;
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (panel) {
          if (expanded) panel.setAttribute('hidden', '');
          else panel.removeAttribute('hidden');
        }
      });
    });
  }

  /* ---------- Cart submission ----------
     Form posts to /cart/add via fetch; on success redirects to /cart.
     Falls back to native form submit if fetch is unavailable.
  */
  function initCartForms() {
    var forms = document.querySelectorAll('[data-pinc-cart-form]');
    forms.forEach(function (form) {
      form.addEventListener('submit', function (e) {
        if (!('fetch' in window) || !('FormData' in window)) return; // native fallback
        e.preventDefault();

        var btn = form.querySelector('button[type="submit"]');
        if (btn) {
          btn.disabled = true;
          btn.setAttribute('data-original-text', btn.textContent);
          btn.textContent = 'Adding...';
        }

        var fd = new FormData(form);
        fetch('/cart/add.js', {
          method: 'POST',
          body: fd,
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (err) { throw err; });
            return r.json();
          })
          .then(function () {
            window.location.href = '/cart';
          })
          .catch(function (err) {
            console.warn('[pinc] add-to-cart failed', err);
            if (btn) {
              btn.disabled = false;
              var original = btn.getAttribute('data-original-text');
              if (original) btn.textContent = original;
            }
            var msg = (err && err.description) || 'Could not add to cart. Please try again.';
            alert(msg);
          });
      });
    });
  }

  /* ---------- Init ---------- */
  function init() {
    initFades();
    initNav();
    document.querySelectorAll('[data-pinc-ingredients]').forEach(initIngredientTabs);
    document.querySelectorAll('[data-pinc-pricing]').forEach(initPricingToggle);
    document.querySelectorAll('[data-pinc-faq]').forEach(initFaq);
    initCartForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();